import { useState, useEffect, useRef } from "react"
import { Pagination, PaginationItem, PaginationLink } from "reactstrap"

const TablePagination = ({ totalItems, itemsPerPage, onPageChange }) => {
    const [currentPage, setCurrentPage] = useState(1)
    const [visiblePages, setVisiblePages] = useState([])
    const paginationRef = useRef(null)

    const totalPages = Math.ceil(totalItems / itemsPerPage)

    useEffect(() => {
        const calculateVisiblePages = () => {
          if (paginationRef.current) {
            const containerWidth = paginationRef.current.offsetWidth
            const buttonWidth = 50 // Approximate width of a page button, adjust as needed
            const availableSpace = containerWidth - 200 // Subtracting space for first/last/prev/next buttons and ellipsis
            let maxVisiblePages = Math.floor(availableSpace / buttonWidth)
            maxVisiblePages = Math.max(7, Math.min(maxVisiblePages, totalPages)) // Ensure at least 7 and at most totalPages

            let newVisiblePages = []
            if (totalPages <= maxVisiblePages) {
                // Show all pages if they fit
                newVisiblePages = Array.from({ length: totalPages }, (_, i) => i + 1)
              } else {
                // Calculate visible pages based on current page
                const sidePages = Math.floor((maxVisiblePages - 5) / 2) // Pages on each side of current page
                const leftEllipsis = currentPage > sidePages + 2
                const rightEllipsis = currentPage < totalPages - sidePages - 1
      
                // Always show first page
                newVisiblePages.push(1)
      
                // Add left ellipsis if needed
                if (leftEllipsis) {
                  newVisiblePages.push('...')
                }
      
                let start
                let end
                if (leftEllipsis && rightEllipsis) {
                  // Both ellipses are shown, center the current page
                  start = Math.max(currentPage - Math.floor((maxVisiblePages - 4) / 2), 2)
                  end = Math.min(start + maxVisiblePages - 5, totalPages - 1)
                  start = Math.max(end - maxVisiblePages + 5, 2) // Adjust start if end is too close to totalPages
                } else if (!leftEllipsis && rightEllipsis) {
                  // Only right ellipsis
                  start = 2
                  end = maxVisiblePages - 2
                } else {
                  // Only left ellipsis
                  end = totalPages - 1
                  start = end - maxVisiblePages + 4
                }
      
                // Add middle pages
                for (let i = start; i <= end; i++) {
                  newVisiblePages.push(i)
                }
      
                // Add right ellipsis if needed
                if (rightEllipsis) {
                  newVisiblePages.push('...')
                }
      
                // Always show last page
                newVisiblePages.push(totalPages)
              }

            setVisiblePages(newVisiblePages)
          }
        }
    
        calculateVisiblePages()
        window.addEventListener('resize', calculateVisiblePages)
    
        return () => {
          window.removeEventListener('resize', calculateVisiblePages)
        }
    }, [currentPage, totalPages])
  
    const handleClick = (page) => {
      setCurrentPage(page)
      onPageChange(page)
    }
  
    const renderPageNumbers = () => {
        return visiblePages.map((page, index) => 
          (page === '...' ? (
            <PaginationItem key={`ellipsis-${index}`} disabled>
              <PaginationLink>...</PaginationLink>
            </PaginationItem>
          ) : (
            <PaginationItem key={page} active={page === currentPage}>
              <PaginationLink onClick={() => handleClick(page)}>
                {page}
              </PaginationLink>
            </PaginationItem>
          ))
        )
      }
  
    return (
        <>
            <div className="paginationContainer" ref={paginationRef}>
                <Pagination>
                    <Pagination className="paginationBox" size="lg">
                        <PaginationItem disabled={currentPage === 1}>
                            <PaginationLink className="tallSymbolFix" first onClick={() => handleClick(1)}>⟪</PaginationLink>
                        </PaginationItem>
                        <PaginationItem disabled={currentPage === 1}>
                            <PaginationLink className="tallSymbolFix" previous onClick={() => handleClick(currentPage - 1)}>⟨</PaginationLink>
                        </PaginationItem>
                    </Pagination>
                    <Pagination className="paginationBox paginationRow" size="lg">
                        {renderPageNumbers()}
                    </Pagination>
                    <Pagination className="paginationBox" size="lg">
                        <PaginationItem disabled={currentPage === totalPages}>
                            <PaginationLink className="tallSymbolFix" next onClick={() => handleClick(currentPage + 1)}>⟩</PaginationLink>
                        </PaginationItem>
                        <PaginationItem disabled={currentPage === totalPages}>
                            <PaginationLink className="tallSymbolFix" last onClick={() => handleClick(totalPages)}>⟫</PaginationLink>
                        </PaginationItem>
                    </Pagination>
                </Pagination>
            </div>
        </>
    )
}
  
export default TablePagination
